import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Grid,
  Headline,
  SelectablePanel,
  Type,
  Utility,
} from "mesh-component-library";
import _isEmpty from "lodash/isEmpty";
import { ContinueAsPpuModal } from "../continue-as-ppu-modal/ContinueAsPpuModal";
import { useLocale } from "../../contexts/LocaleContext";
import { useScreenBreakpoint } from "../../hooks/useScreenBreakpoint";
import {
  getCommonBenefits,
  getPartnerDetails,
  getPpuFlag,
  getServiceInfo,
  getTowCoverages} from "../../selector";
import {
  clearCCardInfo,
  clearPreviousBenefits,
  commonBenefitsSuccess,
  noCommonBenefitsWithPrevious,
  updateCCardInfo,
  updateCurrentLocation,
  updateLoggedInFlag,
  updatePpuFlag,
  updateTowReason} from "../../action";
import { generatePath } from "../../utilities/RedirectUtils";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";

const SomethingElseSteps = () => {
  const getTranslatedText = useLocale();
  const history = useHistory();
  const screenBreakpoint = useScreenBreakpoint();
  const dispatch = useDispatch();

  const [selectedReason, setSelectedReason] = useState("");
  const [showContinueAsPpuModal, setShowContinueAsPpuModal] = useState(false);

  const towCoverages = useSelector(getTowCoverages);
  const { displayCode = "" } = useSelector(getPartnerDetails);
  const ppuFlag = useSelector(getPpuFlag);
  const serviceInfo = useSelector(getServiceInfo);
  const commonBenefits = useSelector(getCommonBenefits);

  useEffect(() => {
    const previousBenefits = commonBenefits?.previousBenefits;
    if(!!previousBenefits && previousBenefits?.benefits) {
      dispatch(updatePpuFlag("N"));
      dispatch(updateLoggedInFlag("Y"));
      dispatch(clearCCardInfo());
      dispatch(commonBenefitsSuccess(previousBenefits));
      dispatch(clearPreviousBenefits());
    }
  }, []);

  const routeToDisablementLocation = () => {
    dispatch(updateTowReason(selectedReason));
    dispatch(updateCurrentLocation({ setFromLocationServices: false }));
    history.push(generatePath("disablementlocation"));
  };

  const handleOnClickContinue = () => {             
    if (_isEmpty(towCoverages) || ppuFlag === "Y") {
      routeToDisablementLocation();
      return;
    }
    const hasCoveredListed = towCoverages.every(({ status }) => status === "covered");
    const hasNotCoveredListed = towCoverages.every(({ status }) => status === "notCovered");
    const coverageSelectedReason = towCoverages.find(({ reason }) => reason === selectedReason);

    if (hasCoveredListed) {
      if (!!coverageSelectedReason) {
        routeToDisablementLocation();
      } else {
        setShowContinueAsPpuModal(true);
      }
      return;
    }

    if (hasNotCoveredListed) {
      if (!!coverageSelectedReason) {
        setShowContinueAsPpuModal(true);
      } else {
        routeToDisablementLocation();
      }
    }
  };

  const handleOnClickContinueAsGuest = () => {
    dispatch(updatePpuFlag("Y"));
    dispatch(updateLoggedInFlag("N"));
    dispatch(updateCCardInfo({ transactionAmount: serviceInfo.originalCost }));
    dispatch(noCommonBenefitsWithPrevious({
        message: "",
        response: commonBenefits,
      })
    );
    setShowContinueAsPpuModal(false);
    routeToDisablementLocation();
  };

  const handleOnClickCancel = () => {
    setShowContinueAsPpuModal(false);
  };

  const SomethingElseTriageReasons = [
    {
      id: "glass_damage",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.OTHER_TRIAGE_GLASS_DAMAGE),
    },
    {
      id: "expired_tags",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.OTHER_TRIAGE_EXPIRED_TAGS),
    },
    {
      id: "covid_recovery",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.UNABLE_TO_RETURN_CAR),
    },
    {
      id: "abandoned",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.LEFT_WHERE_IT_BROKE),
    }
  ];

  return (
    <>
      <Grid verticalAlign="middle" id="tow-triage-page">
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
              vrTop: "md",
              vrBottom: "md",
            }}
          >
            <Headline
              title={
                <Type
                  variant="darker"
                  align="left"
                  size={screenBreakpoint === "sm" ? "7xl" : "3xl"}
                  weight="semibold"
                >
                  {getTranslatedText(TRANSLATION_CONSTANTS.OTHER_STEP_TRIAGE_TITLE)}
                </Type>
              }
            />
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            {SomethingElseTriageReasons.map(({ id: reasonId, reason }, index) => (
              <SelectablePanel
                key={`tow-triage-reason-${reasonId}`}
                id={reasonId}
                checked={selectedReason === reasonId}
                hasBorder
                showInput
                text={reason}
                type="radio"
                onChange={(event) => {
                  setSelectedReason(event.currentTarget.id);
                }}
              />
            ))}
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
          utils={{
            vrTop: "md",
          }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            <Button
              hasUpgrade
              shape="pill"
              size="lg"
              onClick={handleOnClickContinue}
              isBlock
              disabled={_isEmpty(selectedReason)}
            >
              {getTranslatedText(
                TRANSLATION_CONSTANTS.CONTINUE,
                displayCode
              )}
            </Button>
          </Utility.Div>
        </Grid.Column>
      </Grid>
      <ContinueAsPpuModal
        active={showContinueAsPpuModal}
        content={getTranslatedText(
          TRANSLATION_CONSTANTS.TOW_REASON_NOT_COVERED_MSG
        )}
        primaryButton={{
          value: getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE_AS_GUEST),
          onClick: handleOnClickContinueAsGuest,
          testId: "continue-as-guest-primary-button",
        }}
        closeButton={{
          onClick: handleOnClickCancel,
          testId: "continue-as-guest-close-button",
        }}
      />
    </>
  );
};

export default SomethingElseSteps;
